import React, {
  Fragment, useContext, useCallback, useRef, useEffect, useState
} from 'react';
import PropTypes, {
  bool, shape, string, number
} from 'prop-types';
import { ExperienceContext, useStore, useConfigService } from '@thd-nucleus/experience-context';
import { useDomPath, useLifeCycleEventBus } from '@thd-olt-functional/utils';
import { Row, Col } from '@thd-olt-component-react/grid';
import { useLayoutGroupProps } from '@thd-olt-component-react/layout-manager';
import {
  Fulfillment,
  isShowBoth,
  isTwoTile,
  isTwoTileOff,
  isTwoTileQuantityUpdateCookie
} from '@thd-olt-component-react/fulfillment';
import { ProductAddOns } from '@thd-olt-component-react/product-add-ons';
import { SubscribeAndSave } from '@thd-olt-component-react/subscribe-and-save';
import {
  extend,
  useDataModel
} from '@thd-nucleus/data-sources';
import { InstantCheckout } from '@thd-olt-component-react/instant-checkout';
import { AddToQuote } from '@thd-olt-component-react/add-to-quote';
import { Favorite } from '@thd-olt-component-react/save-to-favorites';
import { Alert } from '@thd-olt-component-react/core-ui';

import classNames from 'classnames';
import { BuyboxError } from './subcomponents/BuyboxError';
import { BuyboxUnavailable } from './subcomponents/BuyboxUnavailable';
import { BuyboxCalculator } from './subcomponents/BuyboxCalculator';
import { BuyboxLite } from './subcomponents/BuyboxLite';
import { BuyboxActions } from './subcomponents/BuyboxActions';
import { BuyboxLoader } from './subcomponents/BuyboxLoader';
import { useBuyBox, useBuyboxGetData } from './hooks/useBuyBox';
import * as helper from '../BuyboxHelper';
import * as calculatorHelper from './BuyboxCalcHelper';
import { track } from '../track';
import { dataModel } from './dataModel';
import './buybox.scss';
import { getDeliveryZip, isMobile, isSthAndInStock } from '../util';
import { FULFILLMENT_METHOD_STH } from '../constants';

/**
 * Buybox container
 */
const Buybox = (props) => {
  const {
    channel,
    isServer
  } = useContext(ExperienceContext);

  const buyboxTimerRef = useRef(new Date());

  // use this ref to prevent showing loading placeholder after intial render
  const initialLoadingRef = useRef(true);

  const {
    stash,
    hideQuantity,
    disableATC: buyboxDisableATC,
    lite,
    sticky,
    internally,
    lmUseContextForProps,
    hideInstantCheckout,
    hidePaypalCheckout,
    hideProReferral,
    showCards,
    directData,
    showFavoriteAndQuote
  } = props;

  let {
    itemId,
    isTwoTileEnabledFS
  } = props;
  // convert these to use useConfigService() also
  const {
    bodfsMinimumThreshold,
    bopisMinimumThreshold,
    bopisMinimumThresholdStores,
    bopisHolidayTiming,
    covidAlert
  } = useLayoutGroupProps({ props });

  const isTwoTileEnabledGlobal = useConfigService('isTwoTileEnabled');
  const isTwoTileEnabled = useConfigService('fs-prop:buy-area__buybox--isTwoTileEnabled');
  const isTwoTileQuantityUpdate = useConfigService('isTwoTileQuantityUpdate') || isTwoTileQuantityUpdateCookie();
  const isRisMessageEnabled = useConfigService('fs:isRisMessageEnabled');

  isTwoTileEnabledFS = (
    (isTwoTileEnabledGlobal && isTwoTileEnabled)
    || ((isTwoTile() || isShowBoth()) && !isTwoTileOff())
  );

  const getItemIdFromConfigurator = (items) => {
    if (items) {
      return items.find((item) => item.isAnchor).itemId;
    }
    return '';
  };

  const { storeId, storeZip } = useStore();
  const [buyboxState, setBuyboxState] = useBuyBox({ deliveryZipCode: getDeliveryZip() || storeZip });
  const [updatedQuantity, setQuantityToQuery] = useState(buyboxState.quantity || 1);
  const [subscriptionfrequency, setSubscriptionFrequency] = useState(null);
  const { items } = useLifeCycleEventBus('configurator.configurator_available_items');
  if (items) {
    itemId = getItemIdFromConfigurator(items) || itemId;
  }
  const atcStash = helper.useATCLocationDDOStash({ stash, itemId });

  useEffect(() => {
    // call to set quantity with a delay
    const timer = setTimeout(() => {
      // Update the quantity after a wait so as to avoid unnecessary re-renders.
      if (isTwoTileEnabledFS && isTwoTileQuantityUpdate && !buyboxState.quantityExceeded) {
        setQuantityToQuery(buyboxState.quantity);
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [buyboxState.quantity, buyboxState.quantityExceeded]);

  const { data, loading, error } = useBuyboxGetData({
    itemId,
    storeId,
    directData,
    quantity: isTwoTileEnabledFS && isTwoTileQuantityUpdate ? updatedQuantity : 1,
    zipCode: buyboxState.zipCode
  });

  useEffect(() => {
    if (data?.product) {
      if (typeof LIFE_CYCLE_EVENT_BUS !== 'undefined') {
        const start = buyboxTimerRef.current.getTime();
        const end = new Date().getTime();
        LIFE_CYCLE_EVENT_BUS.trigger('buybox.loaded', { start, end });
      }
    }
  }, [data?.product]);

  const {
    fulfillment = {},
    info = {},
    availabilityType = {},
    pricing = {},
    installServices = {},
    specificationGroup = [],
    identifiers = {},
  } = data?.product || {};
  const parentId = identifiers?.parentId || '';
  const { hasSubscription, minimumOrderQuantity, quantityLimit = 0 } = info || {};

  useEffect(() => {
    if (fulfillment?.fulfillmentOptions) {
      const maximumInventoryQuantity = helper.getFulfillmentMaxQuantity(
        { fulfillment, isTwoTileEnabledFS, qty: buyboxState.quantity });
      setBuyboxState((prevState) => {
        return {
          ...prevState,
          quantityExceeded: buyboxState.quantity > maximumInventoryQuantity
        };
      });
    }
  }, [fulfillment]);

  const onQuantityChange = useCallback(({ quantity }) => {
    const qty = parseInt(quantity, 10);

    // get the inventory available quantity for selected fulfillment
    const maximumInventoryQuantity = helper.getFulfillmentMaxQuantity({ fulfillment, isTwoTileEnabledFS, qty });

    if (!Number.isNaN(qty) && qty > 0) {
      setBuyboxState((prevState) => {
        return {
          ...prevState,
          quantity: qty,
          showTooltip: true,
          quantityExceeded: qty > maximumInventoryQuantity
        };
      });
    }
  }, [buyboxState]);

  const onFulfillmentChange = ({
    currentFulfillment,
    method,
    isShowATC,
    disableATC,
    isOutOfStockOnline,
    quantityLimitExceed,
    availableFulfillments
  }) => {
    setBuyboxState((prevState) => {
      return {
        ...prevState,
        fulfillmentMethod: method,
        availableFulfillments,
        isShowATC,
        disableATC,
        isOutOfStockOnline,
        currentFulfillment,
        quantityLimitExceed,
        // Reset Subscribe & save on fulfillment change
        productAddOns: {
          ...prevState.productAddOns,
          subscriptions: {
            ...prevState.productAddOns.subscriptions,
            frequency: method === FULFILLMENT_METHOD_STH ? subscriptionfrequency : null
          }
        }
      };
    });
  };

  const onProductAddOnChange = (addOnData, productAddOnData) => {

    const { addOn, flags } = helper.getSelectedAddOnInfo(addOnData, buyboxState.fulfillmentMethod);
    setBuyboxState((prevState) => {
      return {
        ...prevState,
        ...flags,
        productAddOns: {
          ...prevState.productAddOns,
          ...addOn
        }
      };
    });
  };

  const onSubscribeAndSaveChange = ({ frequency, isOptIn }) => {
    // isOptIn will be true if they have selected to subscribe
    setBuyboxState((prevState) => {
      return {
        ...prevState,
        productAddOns: {
          ...prevState.productAddOns,
          subscriptions: {
            ...prevState.productAddOns.subscriptions,
            frequency: isOptIn ? frequency : null,
          }
        }
      };
    });
    setSubscriptionFrequency(isOptIn ? frequency : null);
  };

  const onChangeCalculator = ({ quantity, coverage, error: errorCalc }) => {
    setBuyboxState((prevState) => {
      return {
        ...prevState,
        quantity,
        coverage,
        showTooltip: !errorCalc
      };
    });
  };

  const updateAdditionalQuantity = ({ addAdditional, size }) => {
    const { coverage, quantity } = buyboxState;
    let updatedQty = quantity;
    if (coverage) {
      const needed = addAdditional ? +coverage + (+coverage * 0.10) : coverage;
      updatedQty = Math.ceil(needed / size);
    } else if (addAdditional) {
      updatedQty = Math.ceil(+updatedQty + (+updatedQty * 0.10));
    }
    setBuyboxState((prevState) => {
      return {
        ...prevState,
        quantity: updatedQty || quantity,
        addAdditional
      };
    });
  };
  // ######## End of Calculator methods ##########
  const onDeliveryOptionsZipCodeChange = useCallback((shippingDataModel) => {
    const { zipCode } = shippingDataModel;
    setBuyboxState((prevState) => {
      return {
        ...prevState,
        zipCode
      };
    });
  }, []);

  // call back on add to cart click
  const onATCClick = useCallback(() => {
    if (window.LIFE_CYCLE_EVENT_BUS) {
      window.LIFE_CYCLE_EVENT_BUS.trigger('@@ADD-TO-CART/LOCATION', { ...atcStash, parentId });
    }
  }, [itemId, parentId]);

  const unavailable = buyboxDisableATC; // From prop
  const {
    isOutOfStockOnline,
    isShowATC,
    quantity,
    quantityLimitExceed,
    showTooltip,
    addAdditional,
    zipCode,
    fulfillmentMethod,
    paypalEligible,
    productAddOns,
    availableFulfillments,
    assembleItemAtStore,
    assembleItemAtHome,
    quantityExceeded
  } = buyboxState;
  const isATCDisabled = unavailable || buyboxState?.disableATC;
  const showSubscription = (hasSubscription && !isTwoTileEnabledFS)
    || (hasSubscription && isTwoTileEnabledFS && isSthAndInStock({ availableFulfillments, isOutOfStockOnline }));

  const { dualPath = false, virtualAssistance = false } = productAddOns;

  const addOnsAssemblyAndMore = helper.getAddOnsFlagsForFssDrawer({
    productAddOns,
    assembleItemAtStore,
    assembleItemAtHome,
    fulfillmentMethod,
    availableFulfillments
  });

  const orderQuantity = minimumOrderQuantity > quantity ? minimumOrderQuantity : quantity;

  const [pathForAdvisoryMessage, refForAdvisoryMessage] = useDomPath();

  if (error && !data) {
    return (
      <BuyboxError />
    );
  }
  const currentSkuGrouping = info?.productDepartment + '-' + info?.classNumber + '-' + info?.subClassNumber;
  const skuGroupingForReturnInStore = ['25-9-5', '25-9-17', '25-10-38', '25-5-6', '25-12-2',
    '25-12-3', '25-12-4', '25-12-6', '25-12-7', '25-12-15', '25-12-20', '25-28-2',
    '25-28-4', '25-28-5', '25-28-6', '25-28-10', '25-28-11', '25-28-12', '25-28-60',
    '26-2-15', '27-2-4', '27-2-15', '27-3-5', '27-3-6', '27-5-11', '27-8-3', '27-11-4',
    '27-11-8', '27-31-3', '27-31-4', '27-31-7', '27-31-8', '27-31-9', '27-31-14',
    '27-31-19', '27-31-20', '27-31-21', '28-32-6', '29-16-5', '29-16-12', '29-17-2', '29-17-7'];
  const risMessageEligible = skuGroupingForReturnInStore.includes(currentSkuGrouping);
  const twoTilesWithQtyIsLoading = !!(isTwoTileEnabledFS && isTwoTileQuantityUpdate && initialLoadingRef?.current);
  const regular2or3TilesIsLoading = (!isTwoTileQuantityUpdate && loading);

  if (twoTilesWithQtyIsLoading || regular2or3TilesIsLoading || isServer || storeId === '8119') {

    // only for 2tile + qty update: if graphql data is loaded after first page render, stop showing loaders
    if (twoTilesWithQtyIsLoading && data?.product && !loading) {
      initialLoadingRef.current = false;
    }

    if (loading) {
      return (
        <BuyboxLoader
          showCards
          isTwoTileEnabledFS={isTwoTileEnabledFS}
          lite={lite}
          tilesCount={isTwoTileEnabledFS ? 2 : 3}
        />
      );
    }
  }

  if (!data?.product) {
    return null;
  }

  if (unavailable) {
    return (
      <BuyboxUnavailable
        hideQuantity={hideQuantity}
        itemId={itemId}
        quantity={orderQuantity}
        quantityLimitExceed={quantityLimitExceed}
        onQuantityChange={onQuantityChange}
        onFulfillmentChange={onFulfillmentChange}
        onDeliveryOptionsZipCodeChange={onDeliveryOptionsZipCodeChange}
        directData={directData}
      />
    );
  }
  if (lite) {
    return (
      <BuyboxLite
        itemId={itemId}
        configurableItems={items}
        unavailable={unavailable}
        covidAlert={covidAlert}
        storeId={storeId}
        isOutOfStockOnline={isOutOfStockOnline}
        isShowATC={isShowATC}
        storeZip={storeZip}
        isATCDisabled={isATCDisabled}
        channel={channel}
        zipCode={zipCode}
        fulfillmentMethod={fulfillmentMethod}
        fulfillment={fulfillment}
        paypalEligible={paypalEligible}
        availabilityType={availabilityType}
        onFulfillmentChange={onFulfillmentChange}
        onDeliveryOptionsZipCodeChange={onDeliveryOptionsZipCodeChange}
        onATCClick={onATCClick}
        internally={internally}
        bopisHolidayTiming={bopisHolidayTiming}
        lmUseContextForProps={lmUseContextForProps}
        hideInstantCheckout={hideInstantCheckout}
        hidePaypalCheckout={hidePaypalCheckout}
        directData={directData}
        isTwoTileEnabledFS={isTwoTileEnabledFS}
        quantityLimit={quantityLimit}
      />
    );
  }
  const inventoryWarningMessage = (isTwoTileEnabledFS && quantityLimit > 0 && quantity > quantityLimit)
    ? `This item has a limit of ${quantityLimit} per order.`
    : !isOutOfStockOnline
    && isShowATC
    && !!minimumOrderQuantity
    && `We can only ship quantities of ${minimumOrderQuantity} or more`;

  const wrapperClasses = classNames('buybox-wrapper', { 'buybox-wrapper--card-background': showCards });
  const cardClasses = classNames({ 'buybox-top__wrapper': !showCards, buybox__card: showCards });
  const WrapperTag = isMobile(channel) ? Fragment : Row;

  return (
    <WrapperTag>
      <Col fallback="12" nopadding={showCards} className={wrapperClasses} data-component="Buybox">
        <div className={cardClasses}>
          {!unavailable && !virtualAssistance && (
            /* eslint-disable-next-line no-sequences */
            <div className={showCards ? '' : 'buybox__fulfillment'}>
              <Fulfillment
                itemId={itemId}
                covidAlert={covidAlert}
                quantity={orderQuantity}
                onChange={onFulfillmentChange}
                onDeliveryZipCodeChange={onDeliveryOptionsZipCodeChange}
                dualPath={dualPath}
                bodfsMinimumThreshold={bodfsMinimumThreshold}
                bopisMinimumThreshold={bopisMinimumThreshold}
                bopisMinimumThresholdStores={bopisMinimumThresholdStores}
                internally={internally}
                bopisHolidayTiming={bopisHolidayTiming}
                isTwoTileEnabledFS={isTwoTileEnabledFS}
                directData={directData}
                quantityExceeded={quantityExceeded}
                addOnsAssemblyAndMore={addOnsAssemblyAndMore}
              />
            </div>
          )}
        </div>

        {!isOutOfStockOnline && calculatorHelper.shouldDisplayCalculator({ pricing, info }) && !dualPath && (
          <div className={cardClasses}>
            <BuyboxCalculator
              showTooltip={showTooltip}
              addAdditional={addAdditional}
              channel={channel}
              quantity={orderQuantity}
              pricing={pricing}
              info={info}
              fulfillment={fulfillment}
              installServices={installServices}
              specificationGroup={specificationGroup}
              onChangeCalculator={onChangeCalculator}
              updateAdditionalQuantity={updateAdditionalQuantity}
            />
          </div>
        )}

        <>
          <ProductAddOns
            itemId={itemId}
            fulfillment={buyboxState.fulfillmentMethod}
            onChange={onProductAddOnChange}
            availableFulfillments={availableFulfillments}
            hideProReferral={hideProReferral}
            showCards={showCards}
            defaultProps={buyboxState.productAddOns}
            isTwoTileEnabledFS={isTwoTileEnabledFS}
            // Please do not remove the below prop isTwoTileEnabled={isTwoTileEnabledFS}
            // as ProductAddOns has no prop mapping for isTwoTileEnabledFS
            // and still uses isTwoTileEnabled
            isTwoTileEnabled={isTwoTileEnabledFS}
            isShowATC={isShowATC}
          />
          {showSubscription && !isOutOfStockOnline && isShowATC && (
            <div className={cardClasses}>
              <SubscribeAndSave
                itemId={itemId}
                quantity={orderQuantity}
                fulfillment={buyboxState.fulfillmentMethod}
                onChange={onSubscribeAndSaveChange}
              />
            </div>
          )}
        </>

        <div className={cardClasses}>
          {!!inventoryWarningMessage && (
            <>
              {!!pathForAdvisoryMessage.length && track('advisory-message', {
                message: inventoryWarningMessage,
                path: pathForAdvisoryMessage
              })}
              <div className="alert-inline alert-inline--warning inventory--warning" ref={refForAdvisoryMessage}>
                <span>{inventoryWarningMessage}</span>
              </div>
            </>
          )}
          {isRisMessageEnabled && risMessageEligible && (
            <>
              <Row>
                <Col className="alert-inline--info">
                  <Alert type="information" inline message="This item is only returnable in store." />
                </Col>
              </Row>
            </>
          )}
          {!isOutOfStockOnline && isShowATC && !dualPath && !virtualAssistance && (
            <BuyboxActions
              alterBrowserHistory
              disableATC={isATCDisabled}
              fulfillment={fulfillment}
              fulfillmentMethod={fulfillmentMethod}
              hideQuantity={hideQuantity}
              isAssembleItemAtStore={assembleItemAtStore}
              itemId={itemId}
              configurableItems={items}
              minimumOrderQuantity={minimumOrderQuantity}
              onATCClick={onATCClick}
              onQuantityChange={onQuantityChange}
              paypalEligible={paypalEligible}
              productAddOns={productAddOns}
              quantity={orderQuantity}
              quantityLimitExceed={quantityLimitExceed}
              sticky={sticky}
              storeId={storeId}
              storeZip={storeZip}
              zipCode={zipCode}
              showCards={showCards}
              showFavoriteAndQuote={showFavoriteAndQuote}
              isTwoTileEnabledFS={isTwoTileEnabledFS}
              maxQuantityExceeded={quantityExceeded}
              quantityLimit={quantityLimit}
            />
          )}
        </div>
      </Col>
    </WrapperTag>
  );
};

Buybox.displayName = 'Buybox';
Buybox.propTypes = {
  /**
   * The number of clicks you are allowed before seeing a warning.
   */
  itemId: string,
  /** will hide the buybox quantity box */
  hideQuantity: bool,
  /* eslint-disable-next-line react/no-unused-prop-types */
  covidAlert: bool,
  /** Pass ddo values to override the default */
  disableATC: bool,
  lite: bool,
  stash: shape({
    pageType: string,
    sharedSection: string,
    section: string,
    component: string
  }),
  sticky: bool,
  /* eslint-disable-next-line react/no-unused-prop-types */
  bodfsMinimumThreshold: number,
  /* eslint-disable-next-line react/no-unused-prop-types */
  bopisMinimumThreshold: number,
  /* eslint-disable-next-line react/no-unused-prop-types */
  bopisMinimumThresholdStores: string,
  /* eslint-disable-next-line react/no-unused-prop-types */
  isTwoTileEnabledFS: bool,
  internally: bool,
  /* eslint-disable-next-line react/no-unused-prop-types */
  bopisHolidayTiming: number,
  lmUseContextForProps: bool,
  hideInstantCheckout: bool,
  hidePaypalCheckout: bool,
  hideProReferral: bool,
  showCards: bool,
  /* eslint-disable-next-line react/no-unused-prop-types */
  showFavoriteAndQuote: bool,
  /* eslint-disable-next-line react/forbid-prop-types */
  directData: PropTypes.objectOf(PropTypes.any)
};

Buybox.defaultProps = {
  itemId: null,
  lite: false,
  hideQuantity: false,
  disableATC: false,
  stash: {},
  covidAlert: false,
  sticky: false,
  bodfsMinimumThreshold: null,
  bopisMinimumThreshold: null,
  bopisMinimumThresholdStores: null,
  isTwoTileEnabledFS: false,
  internally: false,
  bopisHolidayTiming: null,
  lmUseContextForProps: false,
  hideInstantCheckout: false,
  hidePaypalCheckout: false,
  hideProReferral: false,
  showCards: false,
  showFavoriteAndQuote: false,
  directData: null
};

Buybox.dataModel = extend(
  dataModel,
  Fulfillment,
  ProductAddOns,
  SubscribeAndSave,
  InstantCheckout,
  AddToQuote,
  Favorite
);

export default Buybox;
